// src/pages/TrackCreation/Step1.tsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import shell from "../../pages/track/create/createShell.module.css";

interface Subcategory {
    name: string;
    step: string;
}

interface SidebarItem {
    title: string;
    step?: string;
    hasSubcategories: boolean;
    subcategories?: Subcategory[];
}

const StopsSidebar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Replace the "stops-*" segment in the current URL with the new step.
    const replaceStepInUrl = (newStep: string): string => {
        return location.pathname.replace(/stops-[^/]+/, newStep);
    };

    // Extract a value from the URL using a regex pattern.
    // Matches URLs like "/create/tour/…/stops-*/…/<value>"
    const extractValueFromPattern = (): string | null => {
        const regex = /^\/create\/tour\/[^/]+\/stops-[^/]+\/[^/]+\/([^/]+)$/;
        const match = location.pathname.match(regex);
        return match ? match[1] : null;
    };

    const additionalSubcategories = extractValueFromPattern() !== null;

    const sidebarItems: SidebarItem[] = [
        {
            title: t("stopDetails"),
            hasSubcategories: true,
            subcategories: [
                { name: t("language"), step: "stops-language" },
                ...(additionalSubcategories
                    ? [
                          { name: t("info"), step: "stops-info" },
                          { name: t("activitiesAround"), step: "stops-activities" },
                      ]
                    : []),
            ],
        },
    ];

    const handleBackClick = (): void => {
        const newPath = location.pathname.replace(/\/stops-[^/]+\/.+$/, "/stop-content");
        navigate(newPath);
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, step: string): void => {
        // Optional: add additional handling if needed
    };

    return (
        <div className={`track-create-sidebar-navigation ${shell.navRoot}`}>
            <ul className={`track-create-sidebar-nav ${shell.navList}`}>
                {sidebarItems.map((item, index) => (
                    <div key={index} className={shell.navGroup}>
                        {item.hasSubcategories && item.subcategories ? (
                            <>
                                <li className={`sidebar-title ${shell.navHeading}`}>{item.title}</li>
                                <ul className={`subcategory-list ${shell.navSubList}`}>
                                    {item.subcategories.map((sub, subIndex) => (
                                        <li
                                            key={subIndex}
                                            className={`${shell.navSubItem} ${
                                                location.pathname.includes(sub.step) ? shell.navSubItemActive : ""
                                            }`}
                                        >
                                            <Link
                                                to={replaceStepInUrl(sub.step)}
                                                className="sidebar-link"
                                                onClick={(e) => handleLinkClick(e, sub.step)}
                                            >
                                                {sub.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            <li
                                className={`sidebar-title ${shell.navTitleLink} ${
                                    location.pathname.includes(item.step || "") ? shell.navTitleLinkActive : ""
                                }`}
                            >
                                <Link
                                    to={replaceStepInUrl(item.step || "")}
                                    className="sidebar-link"
                                    onClick={(e) => handleLinkClick(e, item.step || "")}
                                >
                                    {item.title}
                                </Link>
                            </li>
                        )}
                    </div>
                ))}
            </ul>
            <Button
                label={t("backToStops")}
                variant="outline"
                shape="pill"
                className={shell.navBackBtn}
                onClick={handleBackClick}
            />
        </div>
    );
};

export default StopsSidebar;
